import { EmptyState, Table, type TableColumn } from '@today/ui-components'
import { Button, Dialog } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import type { FormState, LegalHoldFormValues, LegalHoldRowView, Translate } from './compliance-types'
import { FormAlert, TextInput } from './form-parts'

/**
 * Props for {@link LegalHoldPanelView}. `form` and `onRelease` are present only
 * for a viewer holding `compliance.legal_hold.manage`.
 *
 * @property rows - The tenant's active holds.
 * @property message - The last release's refusal, or null.
 * @property releaseTarget - The hold awaiting release confirmation, or null.
 */
export interface LegalHoldPanelViewProps {
	t: Translate
	rows: LegalHoldRowView[]
	error: boolean
	message: string | null
	form?: FormState<LegalHoldFormValues>
	onRelease?: (id: string) => void
	releaseTarget: LegalHoldRowView | null
	onReleaseAccept: () => void
	onReleaseDismiss: () => void
	onRetry: () => void
}

const columns = (t: Translate, onRelease?: (id: string) => void): TableColumn<LegalHoldRowView>[] => [
	{ key: 'customerId', header: t('holds.columns.customer') },
	{ key: 'reason', header: t('holds.columns.reason') },
	{ key: 'placedAt', header: t('holds.columns.placed') },
	...(onRelease
		? [
				{
					key: 'actions',
					header: '',
					cell: (row: LegalHoldRowView) => (
						<Button variant="muted" size="sm" onPress={() => onRelease(row.id)}>
							{t('holds.release')}
						</Button>
					),
				},
			]
		: []),
]

function PlaceHoldForm({ t, form }: { t: Translate; form: FormState<LegalHoldFormValues> }): ReactElement {
	const title = t('holds.form.title')
	return (
		<form
			aria-label={title}
			className="flex flex-col gap-3"
			onSubmit={(event) => {
				event.preventDefault()
				form.onSubmit()
			}}
		>
			<h3 className="text-ds-base font-semibold text-ds-text">{title}</h3>
			<TextInput
				label={t('holds.form.customerId')}
				value={form.values.customerId}
				error={form.errors.customerId}
				onChange={(v) => form.onChange('customerId', v)}
			/>
			<TextInput
				label={t('holds.form.reason')}
				value={form.values.reason}
				error={form.errors.reason}
				onChange={(v) => form.onChange('reason', v)}
			/>
			<FormAlert message={form.message} />
			<Button type="submit" isDisabled={form.submitting}>
				{t('holds.form.submit')}
			</Button>
		</form>
	)
}

/** Active legal holds, the "Place legal hold" form and a confirmed release (organism). */
export function LegalHoldPanelView(props: LegalHoldPanelViewProps): ReactElement {
	const { t } = props
	return (
		<section className="flex flex-col gap-4">
			<h2 className="text-ds-lg font-semibold text-ds-text">{t('holds.title')}</h2>
			<FormAlert message={props.message} />
			{props.error ? (
				<EmptyState title={t('holds.error')} action={{ label: t('common.retry'), onPress: props.onRetry }} />
			) : (
				<Table
					aria-label={t('holds.title')}
					columns={columns(t, props.onRelease)}
					rows={props.rows}
					emptyState={<EmptyState title={t('holds.empty')} />}
				/>
			)}
			{props.form ? <PlaceHoldForm t={t} form={props.form} /> : null}
			<Dialog
				isOpen={props.releaseTarget !== null}
				onOpenChange={(open) => (open ? undefined : props.onReleaseDismiss())}
				title={t('holds.releaseTitle')}
			>
				<p className="text-ds-sm text-ds-text">
					{t('holds.releaseBody', { customerId: props.releaseTarget?.customerId ?? '' })}
				</p>
				{/* The modal hides the page behind it, so a refusal is repeated where the officer is looking. */}
				<FormAlert message={props.message} />
				<Button onPress={props.onReleaseAccept}>{t('holds.releaseConfirm')}</Button>
			</Dialog>
		</section>
	)
}
